import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, MapPin } from 'lucide-react';
import { RESIDENCES as DATA } from '../data/content';
import { RESIDENCES_PAGE } from '../constants/testIds';
import PageHero from '../components/aravi/PageHero';
import MasterplanSection from '../components/aravi/MasterplanSection';
import Enquiry from '../components/aravi/Enquiry';

const REGIONS = ['All', ...Array.from(new Set(DATA.map((d) => d.region)))];

export default function ResidencesPage() {
  const [filter, setFilter] = useState('All');

  const visible = useMemo(
    () => (filter === 'All' ? DATA : DATA.filter((r) => r.region === filter)),
    [filter]
  );

  return (
    <div data-testid={RESIDENCES_PAGE.root}>
      <PageHero
        eyebrow="Homes at Aravi"
        title={<>Two homes.<br/>One nature crafted land.</>}
        intro="Choose the 605 or the 1,210 square yard configuration — each comes with a finished Ground+1 home, customisable and expandable at a clear, published per-square-foot rate."
        image="https://images.unsplash.com/photo-1706855203772-c249b75fe016?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBmb3Jlc3QlMjBob21lJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MjczMTIzNnww&ixlib=rb-4.1.0&q=85"
      />

      <section className="bg-[#F5F1E8] py-20 lg:py-28">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-12">
            <span className="text-[10px] tracking-[0.4em] uppercase text-[#6D6A61] mr-2">
              Filter
            </span>
            {REGIONS.map((r) => (
              <button
                key={r}
                type="button"
                data-testid={RESIDENCES_PAGE.filter(r)}
                onClick={() => setFilter(r)}
                className={`px-5 py-2.5 text-[11px] tracking-[0.28em] uppercase border transition-colors ${
                  filter === r
                    ? 'bg-[#18342A] text-[#F5F1E8] border-[#18342A]'
                    : 'border-[#18342A]/25 text-[#18342A] hover:bg-[#18342A] hover:text-[#F5F1E8] hover:border-[#18342A]'
                }`}
              >
                {r}
              </button>
            ))}
          </div>

          {/* Grid */}
          {visible.length === 0 ? (
            <div
              data-testid={RESIDENCES_PAGE.empty}
              className="py-20 text-center text-[#6D6A61] font-light"
            >
              No residences in this region yet. A new release is on the way.
            </div>
          ) : (
            <div
              data-testid={RESIDENCES_PAGE.grid}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10"
            >
              {visible.map((r, i) => (
                <motion.article
                  key={r.id}
                  data-testid={RESIDENCES_PAGE.gridCard(r.slug)}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className="group"
                >
                  <Link
                    to={`/residences/${r.slug}`}
                    data-testid={RESIDENCES_PAGE.gridCardLink(r.slug)}
                    className="block"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={r.image}
                        alt={r.name}
                        className="w-full aspect-[4/5] object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-[#F5F1E8]/85 backdrop-blur-md text-[#18342A] text-[10px] tracking-[0.3em] uppercase px-3 py-1.5">
                          {r.status}
                        </span>
                      </div>
                      <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-[#18342A]/85 to-transparent text-[#F5F1E8]">
                        <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#C9A458] mb-2">
                          <MapPin size={12} strokeWidth={1.5} />
                          {r.location}
                        </div>
                        <h3 className="font-serif text-2xl md:text-3xl leading-tight">{r.name}</h3>
                      </div>
                    </div>
                  </Link>

                  <div className="mt-5 flex items-center justify-between">
                    <div className="flex items-center gap-5 text-[#6D6A61]">
                      <Spec label="Beds" value={r.beds} />
                      <span className="w-px h-4 bg-[#6D6A61]/30" />
                      <Spec label="Baths" value={r.baths} />
                      <span className="w-px h-4 bg-[#6D6A61]/30" />
                      <Spec label="Acres" value={r.acres} />
                    </div>
                    <Link
                      to={`/residences/${r.slug}`}
                      aria-label={`Open ${r.name}`}
                      className="w-10 h-10 grid place-items-center border border-[#18342A]/30 text-[#18342A] hover:bg-[#C9A458] hover:border-[#C9A458] transition-colors"
                    >
                      <ArrowUpRight size={16} strokeWidth={1.4} />
                    </Link>
                  </div>
                  <div className="mt-4 flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-[#A66B45]">
                    <span>From {r.priceFrom}</span>
                    {r.priceNote && (
                      <>
                        <span className="w-1 h-1 rounded-full bg-[#A66B45]/60" />
                        <span className="text-[#6D6A61] normal-case tracking-normal text-xs">{r.priceNote}</span>
                      </>
                    )}
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <MasterplanSection />

      <Enquiry />
    </div>
  );
}

const Spec = ({ label, value }) => (
  <div className="flex flex-col items-start">
    <span className="font-serif text-[#18342A] text-xl leading-none">{value}</span>
    <span className="mt-1 text-[9px] tracking-[0.35em] uppercase text-[#6D6A61]">{label}</span>
  </div>
);
